/* eslint-disable no-param-reassign */
import { useCallback, useState } from 'react';
import { isEmpty, isEqual, keyBy } from 'lodash';
import { AllocationsService, ApiService, MultiAllocationInputSerializer, SecurityModel } from 'api';
import { OPM } from 'common/constants/allocations';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { useResponse } from 'services/hooks';
import { currentISODate, toString, validateWeighting } from 'utilities';
import { ColumnWithWeightProps } from 'utilities/validateWeighting/validateWeighting';
import calculateWeightedPerShareValues from './calculateWeightedPerShareValue';
import { UpdateScenarioValuesProps } from './types';

function getAndAddCell(spreadsheets: any, onCellsChanged: any) {
  return (s: any) => {
    const cellKey = `${s.security.id}-${s.col}`;
    const cell =
      spreadsheets.backsolveSheet.cells[cellKey] ??
      Object.values(spreadsheets.backsolveSheet.cells).find(
        (c: any) => c.securityName === s.security.name && c.y === s.col
      );
    if (cell) {
      const value = s.value ?? 0;
      onCellsChanged(cell, value);
    }
  };
}

function checkEmptyRows(newDistribution: any, spreadsheets: any, onCellsChanged: any, data: any) {
  const newRows = newDistribution.map((dist: any) => dist.presentValues);
  if (!isEmpty(newRows)) {
    newRows.forEach((securities: any) => {
      if (!isEmpty(securities)) {
        securities.forEach(getAndAddCell(spreadsheets, onCellsChanged));
      }
    });

    const appliedMethodologies = data.applied_methodologies;
    const totalPresentShareValues = calculateWeightedPerShareValues(newRows, appliedMethodologies);

    data.basket.forEach((b: any, i: number) => {
      const presentValue = totalPresentShareValues.find(
        (tpsv: any) => Number(tpsv.securityId) === Number(b.security.id)
      );
      const cell = spreadsheets.securitySheet.cells[`C${i + 2}`];

      onCellsChanged(cell, presentValue?.value ?? 0);
    });

    spreadsheets.securitySheet.tableData.securitiesBasket.target_value = data.target_value;
  }
}

function changeIfBacksolveSummarySheet(spreadsheets: any, onCellsChanged: any, equityValue: number) {
  if (spreadsheets.backsolveSummarySheet) {
    onCellsChanged(spreadsheets.backsolveSummarySheet.cells.B2, equityValue);
  }
}

const populateBasket = (currentNonZeroRows: any, securityList: SecurityModel[]) => {
  const currentBasket: any = [];
  currentNonZeroRows.forEach((row: any) => {
    const security = securityList?.find((s: any) => toString(s.id) === toString(row.security));

    if (security) {
      currentBasket.push({
        security: {
          id: security.id,
          name: security.name,
        },
        shares: Number(row.shares),
        value: Number(row.value),
      });
    }
  });
  return currentBasket;
};

const populateAppliedMethodologies = (appliedMethodologies: any, maturity: number, volatility: number, riskFreeRate: number) => {
  const methodologies = appliedMethodologies.map((m: any) => ({
    allocation_method: m.allocation_method,
    cap_table: Number(m.cap_table),
    weight: parseFloat(m.weight),
    maturity: Number(m.allocation_method) === Number(OPM) ? maturity : 0,
    volatility: Number(m.allocation_method) === Number(OPM) ? volatility : 0,
    risk_free_rate: Number(m.allocation_method) === Number(OPM) ? riskFreeRate : 0,
    opm_backsolve_date: m.opm_backsolve_date,
  }));

  return methodologies as ColumnWithWeightProps[];
};

const getOPMValuesFromCells = (opmData: any) => {
  const { maturity: mCell, volatility: vCell, risk_free_rate: rfrCell, opm_backsolve_date } = keyBy(opmData, 'alias');
  const maturity = Number(mCell?.value ?? 0);
  const backsolveDate = opm_backsolve_date?.value;
  const volatility = Number(vCell?.value ?? 0);
  const riskFreeRate = Number(rfrCell?.value ?? 0);
  return { maturity, volatility, riskFreeRate, backsolveDate };
};

const mapValuesWithMethodology = (values: any, index: number, methodology: any) =>
  values?.map((v: any) => ({ ...v, col: index + 1, allocationMethod: methodology.allocation_method })) ?? [];

const useCalculateEquityValue = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [scenarioEquityValue, setScenarioEquityValue] = useState<number>(0);
  const [prevAllocationParams, setPrevAllocationParams] = useState<any>({});
  const { processErrorResponse } = useResponse();

  const fetchScenarioEquityValue = useCallback(async (data: MultiAllocationInputSerializer) => {
    const response = await AllocationsService.apiAllocationsCalculateBacksolveCreate(data);

    if (response) {
      const { equity_value: equityValue } = response;
      return equityValue;
    }

    return 0;
  }, []);

  const updateScenarioValues = async ({
    backsolveData,
    exitDate,
    equityValue,
    scenarioType,
    marketDate,
  }: UpdateScenarioValuesProps) => {
    const { applied_methodologies: methodologies } = backsolveData;

    const scenarioValues = await Promise.all(
      methodologies.map(async (m: any, index: number) => {
        const response = await ApiService.apiAllocationScenarioAllocationResultsCreate({
          scenarioType,
          exitDate,
          equityValue,
          captableId: m.cap_table,
          scenarioMethod: m.allocation_method,
          maturity: m.maturity,
          volatility: m.volatility,
          riskFreeRate: m.risk_free_rate,
          marketDate,
        });

        if (!response) {
          return {};
        }

        const {
          present_values: presentValues,
          aggregate_values: aggregateValues,
          future_values: futureValues,
        } = response;

        return {
          presentValues: mapValuesWithMethodology(presentValues, index, m),
          aggregateValues: mapValuesWithMethodology(aggregateValues, index, m),
          futureValues: mapValuesWithMethodology(futureValues, index, m),
        };
      })
    );

    return scenarioValues;
  };

  const calculateEquityValue = useCallback(
    async ({
      scenarioType,
      allocation,
      appliedMethodologies,
      // opmData is the opmTable cells
      opmData,
      securityRows,
      targetValue,
      prevOpmData,
      editableLedgerCells,
      prevEditableLedgerCells,
      targetValueData,
      prevTargetValueData,
      securityList,
      spreadsheets,
      onCellsChanged,
    }: {
      scenarioType: number;
      allocation: number;
      opmData: ICell[];
      securityList: SecurityModel[];
    }) => {
      const exitDate = currentISODate();
      const rows = securityRows;
      const { maturity, volatility, riskFreeRate } = getOPMValuesFromCells(opmData);

      const currentNonZeroRows = securityRows.filter((row: any) => row.shares > 0);

      const getScenarioData = () => {
        const basket = populateBasket(currentNonZeroRows, securityList);
        const appliedMethodologiesData = populateAppliedMethodologies(appliedMethodologies, maturity, volatility, riskFreeRate);

        const requestData = {
          applied_methodologies: appliedMethodologiesData,
          target_value: Number(targetValue),
          basket,
          allocation,
        };

        return requestData;
      };

      const { maturity: prevMaturity, volatility: prevVolatility, risk_free_rate: prevRiskFreeRate } = keyBy(prevOpmData, 'alias');

      const cellsChanged = !isEqual(editableLedgerCells, prevEditableLedgerCells);
      const targetValueChanged = !isEqual(targetValueData, prevTargetValueData);
      const maturityChanged = toString(maturity) !== toString(prevMaturity?.value);
      const volatilityChanged = toString(volatility) !== toString(prevVolatility?.value);
      const riskFreeRateChanged = toString(riskFreeRate) !== toString(prevRiskFreeRate?.value);

      const dataChanged = cellsChanged || targetValueChanged || maturityChanged || volatilityChanged || riskFreeRateChanged;

      const data = getScenarioData();

      const backsolveDate = data?.applied_methodologies?.find((am: any) => am.opm_backsolve_date)?.opm_backsolve_date;
      const validateData = () => {
        let isValid = true;

        const columnsWithWeights = data.applied_methodologies;
        const isValidWeight = validateWeighting({ columnsWithWeights });

        isValid = data.basket.length === rows.length && targetValue && allocation && isValidWeight;
        const existsOPM = data.applied_methodologies.some(am => Number(am.allocation_method) === Number(OPM));

        if (existsOPM) {
          data.applied_methodologies
            .filter(am => Number(am.allocation_method) === Number(OPM))
            .forEach(am => {
              if (!(am.maturity && am.maturity > 0 && am.volatility && am.volatility > 0 && am.risk_free_rate)) {
                isValid = false;
              }
            });
        }

        return isValid;
      };

      const dataIsValid = validateData();

      if (dataChanged && dataIsValid) {
        let returnValue = {};
        try {
          setIsLoading(true);

          const equityValue = await fetchScenarioEquityValue(data);
          const allocationParams = {
            backsolveData: data,
            exitDate,
            marketDate: backsolveDate ?? null, // only used when calculating backsolve OPM so we don't mind if it's null for other methodologies
            equityValue,
            scenarioType,
          };

          setScenarioEquityValue(equityValue);
          let newDistribution = [];
          if (!isEqual(allocationParams, prevAllocationParams)) {
            newDistribution = await updateScenarioValues(allocationParams);
            setPrevAllocationParams(allocationParams);
            checkEmptyRows(newDistribution, spreadsheets, onCellsChanged, data);
          }
          changeIfBacksolveSummarySheet(spreadsheets, onCellsChanged, equityValue);
          returnValue = { equityValue, newDistribution };
        } catch (error: any) {
          const errorMessages = JSON.parse(error.response.text);
          const [tmpMessages] = Object.values(errorMessages);

          processErrorResponse({
            error,
            defaultErrorMessage: `${(tmpMessages as string[]).join('. ')}.`,
            action: 'get scenario equity value',
          });
        } finally {
          setIsLoading(false);
        }
        return returnValue;
      }
      return {};
    },
    [fetchScenarioEquityValue, prevAllocationParams, processErrorResponse]
  );

  return {
    calculateEquityValue,
    isLoading,
    scenarioEquityValue,
  };
};

export default useCalculateEquityValue;
